import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  currentUserSelector,
  fetchProfileUser,
  setOpenUsersListModal,
  setProfileUser,
  setUsersListView,
  ulistSelector,
  setFollowButtonLoading,
  fetchUpdatedFollowers,
  viewUserSelector,
  followingSelector,
  unfollowingSelector,
} from '../redux/slices/userSlice';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { setHeaderTitle } from '../redux/slices/layoutSlice';
import useAchievementTracking from './useAchievementTracking';
import useUserContext from './useUserContext';
import { Notification, NotificationType, User } from '../types';
import addNotification from '../services/notificationService';

/**
 * Custom hook that handles the settings click.
 * @returns - the handleSettingsClick and handleFollowersFollowingClick function.
 */
const useProfilePage = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { socket } = useUserContext();
  const curUrlUser = location.pathname.split('/profile/')[1].split('?')[0];
  const viewUser = useAppSelector(viewUserSelector);
  const currentUser = useAppSelector(currentUserSelector);
  const users = useAppSelector(ulistSelector);
  const followLoading = useAppSelector(followingSelector);
  const unfollowLoading = useAppSelector(unfollowingSelector);
  const [followTrigger, setFollowTrigger] = useState(false);
  const [viewUserUpdated, setViewUserUpdated] = useState(true);
  const [prevFollowers, setPrevFollowers] = useState<User[]>([]);

  const { addAchievementIfEarned } = useAchievementTracking({
    achievementType: 'follow',
  });

  useEffect(() => {
    if (curUrlUser === viewUser?.username) {
      dispatch(setHeaderTitle(currentUser?._id === viewUser?._id ? 'My Profile' : 'User Profile'));
    } else {
      const profileUser = users?.find(user => user.username === curUrlUser);
      dispatch(setProfileUser(profileUser));
    }
  }, [dispatch, curUrlUser, viewUser, currentUser, users]);

  const [followButtonDisplay, setFollowButtonDisplay] = useState('');

  useEffect(() => {
    const updateFollowButtonDisplay = () => {
      if (followLoading) {
        setFollowButtonDisplay('Following...');
      } else if (unfollowLoading) {
        setFollowButtonDisplay('Unfollowing...');
      } else if (
        viewUser &&
        currentUser &&
        currentUser.following.some(user => user._id === viewUser._id)
      ) {
        setFollowButtonDisplay('Unfollow');
      } else if (
        viewUser &&
        currentUser &&
        currentUser?.followers.some(user => user._id === viewUser._id)
      ) {
        setFollowButtonDisplay('Follow Back');
      } else {
        setFollowButtonDisplay('Follow');
      }
    };
    updateFollowButtonDisplay();
  }, [currentUser, followLoading, unfollowLoading, viewUser]);

  useEffect(() => {
    if (viewUser && viewUser.followers.length !== prevFollowers.length) {
      setViewUserUpdated(true);
    }
  }, [prevFollowers.length, viewUser]);

  useEffect(() => {
    const updateAchievements = async () => {
      if (!viewUser || !currentUser) return;
      await addAchievementIfEarned(
        viewUser,
        viewUser,
        achievement => achievement.goal === (viewUser.followers?.length || 0),
        achievement => achievement.progress <= viewUser.followers.length,
      );
      // new changes should display for the followed user
      dispatch(fetchProfileUser({ identifier: viewUser._id || '', idType: '_id' }));
    };
    if (followTrigger && viewUserUpdated) {
      // only send if followers are increasing
      if ((viewUser?.followers || []).length > prevFollowers.length) {
        // Only proceed if the followTrigger is true
        updateAchievements();
        const notif: Notification = {
          message: `{sender} started following you`,
          sender: currentUser as User,
          receiver: viewUser as User,
          notificationType: NotificationType.Follow,
          notificationDateTime: new Date(),
          path: `/profile/{sender}`,
        };
        addNotification(notif);
      }
      // reset state
      setFollowTrigger(prev => !prev);
      setViewUserUpdated(false);
      setPrevFollowers(viewUser?.followers || []);
    }
  }, [
    addAchievementIfEarned,
    currentUser,
    dispatch,
    viewUser,
    followTrigger,
    viewUserUpdated,
    prevFollowers.length,
    users,
  ]);

  /**
   * Handles the followers or following click.
   * @param type - determines if the user clicked on followers or following.
   */
  const handleFollowersFollowingClick = (type: 'followers' | 'following') => {
    dispatch(setUsersListView(type));
    dispatch(setOpenUsersListModal(true));
  };

  /**
   * Handles the follow or unfollow click.
   * @param isFollowing - determines if the user is following or unfollowing.
   */
  const handleFollowEvent = async (follower: string, following: string) => {
    const action = followButtonDisplay === 'Unfollow' ? 'unfollow' : 'follow';
    const followingUser = users?.find(u => u._id === following);
    if (follower && followingUser) {
      dispatch(setFollowButtonLoading(action));
      dispatch(fetchUpdatedFollowers({ follower, following, action }));
      setViewUserUpdated(false);
      setFollowTrigger(true);
    }
  };

  useEffect(() => {
    const handleFollowUpdate = (uid: string) => {
      if (viewUser?._id === uid)
        dispatch(fetchProfileUser({ identifier: uid || '', idType: '_id' }));
    };

    socket.on('followUpdate', handleFollowUpdate);

    return () => {
      socket.off('followUpdate', handleFollowUpdate);
    };
  }, [viewUser, dispatch, socket, users]);

  const [questionsExpanded, setQuestionsExpanded] = useState(false);
  const [answersExpanded, setAnswersExpanded] = useState(false);
  const [commentsExpanded, setCommentsExpanded] = useState(false);

  return {
    curUrlUser,
    viewUser,
    currentUser,
    followButtonDisplay,
    handleFollowEvent,
    handleFollowersFollowingClick,
    questionsExpanded,
    setQuestionsExpanded,
    answersExpanded,
    setAnswersExpanded,
    commentsExpanded,
    setCommentsExpanded,
  };
};

export default useProfilePage;
